'use client';

import { Box, Button, HStack, List, Separator, Spacer, Stack } from '@chakra-ui/react';
import { NavigateBar } from '../components/NavigateBar';
import { FileUploadDropzone, FileUploadList, FileUploadRoot } from '../components/ui/file-upload';
import { Text } from '../components/Text';
import { Field } from '../components/ui/field';
import { PasswordInput } from '../components/ui/password-input';
import { IoAdd, IoCloseOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
    DialogActionTrigger,
    DialogBody,
    DialogCloseTrigger,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    DialogTitle,
} from '../components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { FileStore, useStore } from '../store';

const readDropedFile = async (file: any, asText: boolean): Promise<string | null> => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
        try {
            reader.onloadend = () => {
                resolve(reader.result as string);
            };
            if (asText) {
                reader.readAsText(file);
            } else {
                reader.readAsArrayBuffer(file);
            }
        } catch (error) {
            reject(error);
        }
    });
};

// Funktion, um einen ArrayBuffer in Base64 zu kodieren
const arrayBufferToBase64 = (buffer: any) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
};

export const FileUpload = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
    const [showPinReplaceDialog, setShowPinReplaceDialog] = useState(false);
    const { fileStore, fileReplaceStore, setFileStore, setFileReplaceStore } = useStore();

    const deleteValue = (index: number) => {
        if (fileReplaceStore.length === 1) {
            setFileReplaceStore(['']);
        } else {
            const tmp = [...fileReplaceStore];
            tmp.splice(index, 1);
            setFileReplaceStore([...tmp]);
        }
    };

    const prepareUploadedFiles = async (): Promise<void> => {
        const newFiles: FileStore[] = [];
        for (const _file of droppedFiles) {
            const asHar = _file.type === '' && _file.name.endsWith('.har');
            let content = await readDropedFile(_file, asHar);

            if (!content) continue;

            if (asHar) {
                fileReplaceStore.forEach((str) => {
                    content = content!.replace(new RegExp(str, 'g'), new Array(str.length + 1).join('X'));
                });
                content = btoa(encodeURI(content as string));
            } else {
                content = arrayBufferToBase64(content);
            }
            newFiles.push({ name: _file.name, size: _file.size, type: asHar ? 'har' : _file.type, content });
        }
        setFileStore([...fileStore, ...newFiles]);
        navigate('/submit');
    };

    let overloadContinueButton: (() => void) | undefined = undefined;
    if (fileReplaceStore.filter((item) => item.trim()).length < 1) {
        // Ask customer if he will not replace any pin values?
        overloadContinueButton = async () => {
            setShowPinReplaceDialog(true);
        };
    } else {
        overloadContinueButton = prepareUploadedFiles;
    }

    return (
        <>
            <Stack spaceY={4}>
                <Field label="fileUploadScreen.replaceFields">
                    {fileReplaceStore.map((item, index) => {
                        return (
                            <HStack width="100%">
                                <PasswordInput
                                    key={index}
                                    name={`replace-${index}`}
                                    value={item}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const tmp = [...fileReplaceStore];
                                        tmp[index] = e.target.value;
                                        setFileReplaceStore([...tmp]);
                                    }}
                                />
                                <IoCloseOutline size={22} onClick={() => deleteValue(index)} />
                                {index + 1 === fileReplaceStore.length && (
                                    <IoAdd onClick={() => setFileReplaceStore([...fileReplaceStore, ''])} size={22} />
                                )}
                            </HStack>
                        );
                    })}
                </Field>
                <FileUploadRoot
                    data={droppedFiles}
                    alignItems="stretch"
                    maxFiles={10}
                    accept={['image/png', 'image/jpeg', '.har', '.zip', '.mov', '.mp4']}
                    onFileChange={(details: any) => {
                        setDroppedFiles(details.acceptedFiles as File[]);
                    }}
                >
                    <FileUploadDropzone
                        label={t('fileUploadScreen.dragAndDrop')}
                        description={t('fileUploadScreen.dragAndDropDescription')}
                    />
                    <FileUploadList clearable />
                </FileUploadRoot>
            </Stack>

            {fileStore.length > 0 && (
                <Box>
                    <Separator mt={4} mb={4} />
                    <Text trans="fileUploadScreen.alreadyAddedFiles" />
                    <List.Root>
                        {fileStore.map((item) => (
                            <List.Item>{item.name}</List.Item>
                        ))}
                    </List.Root>
                    <HStack>
                        <Spacer />
                        <Button
                            variant="outline"
                            rounded="lg"
                            fontWeight="600"
                            bg="light.100"
                            borderColor="brand.100" 
                            color="brand.100"
                            onClick={() => setFileStore([])}>
                            {t('clear')}
                        </Button>
                    </HStack>
                    <Separator mt={4} />
                </Box>
            )}
            <NavigateBar
                mt={10}
                backPath="/general"
                continuePath="/submit"
                disableContinueButton={!(fileStore.length > 0 || droppedFiles.length > 0)}
                overloadContinueFn={overloadContinueButton}
            />
            <DialogRoot size="md" open={showPinReplaceDialog} motionPreset="slide-in-bottom">
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('generalScreen.dialogPinReplaceTitle')}</DialogTitle>
                    </DialogHeader>
                    <DialogBody>{t('generalScreen.dialogPinReplaceMessage')}</DialogBody>
                    <DialogFooter>
                        <DialogActionTrigger asChild>
                            <Button
                                onClick={async (): Promise<void> => {
                                    setShowPinReplaceDialog(false);
                                    await prepareUploadedFiles();
                                    navigate('/submit');
                                }}
                                variant="outline"
                                rounded="lg"
                                fontWeight="600"
                                bg="light.100"
                                borderColor="brand.100" 
                                color="brand.100"
                            >
                                {t('continue')}
                            </Button>
                        </DialogActionTrigger>
                        <DialogActionTrigger asChild>
                            <Button
                                onClick={() => {
                                    setShowPinReplaceDialog(false);
                                }}
                                rounded="lg"
                                fontWeight="600"
                            >
                                {t('abort')}
                            </Button>
                        </DialogActionTrigger>
                    </DialogFooter>
                    <DialogCloseTrigger />
                </DialogContent>
            </DialogRoot>
        </>
    );
};
